import { state } from '../state'
import { ads } from './ads'
import { createCmsClient, type CmsClient, type CmsEntry, type CmsList } from '../cms-client'
import { MONTILLA_HOSTS, type Environment } from '../constants'

/**
 * `montilla.cms.*` — a superfície guarda-chuva do Montilla CMS no browser.
 *
 * Reúne os DOIS eixos: preencher um buraco na página (que era `montilla.ads`) e
 * buscar conteúdo por endereço. Uma função de render só, como um componente só:
 * o eixo decidido × endereçado vive no `binding` do slot, não na assinatura —
 * o binding é DADO, e o operador precisa re-apontar um slot sem deploy.
 *
 * `montilla.ads.*` continua existindo como alias, para sempre. O custo é uma
 * linha, e há site em produção chamando aquele nome.
 */

/**
 * Onde o rum-api atende. MESMA resolução do `fetchAds`, de propósito: os dois
 * eixos falam com o mesmo host, e duas resoluções divergiriam no primeiro
 * ambiente que precisasse de override.
 */
function resolveBaseUrl(): string {
  const env: Environment = state.config?.app?.environment ?? 'production'
  return (state.config?.ads?.url ?? MONTILLA_HOSTS[env]).replace(/\/$/, '')
}

let client: CmsClient | null = null
let clientBase: string | null = null

/** Recria o cliente quando a base muda (troca de ambiente em teste, sobretudo). */
function cms(): CmsClient {
  const base = resolveBaseUrl()
  if (!client || clientBase !== base) {
    client = createCmsClient({ baseUrl: base })
    clientBase = base
  }
  return client
}

/**
 * Uma entrada por endereço. Lança quando não existe.
 * @example montilla.cms.get('blog/guia-do-enem')
 */
function get(path: string, options?: { locale?: string }): Promise<CmsEntry> {
  return cms().get(path, options)
}

/** Como {@link get}, mas devolve null no miss — para bloco opcional. */
function find(path: string, options?: { locale?: string }): Promise<CmsEntry | null> {
  return cms().find(path, options)
}

/**
 * Uma coleção, mais recém-publicadas primeiro.
 * @example montilla.cms.list('blog', { limit: 10 })
 */
function list(
  collection: string,
  options?: { locale?: string, limit?: number, offset?: number },
): Promise<CmsList> {
  return cms().list(collection, options)
}

/** Só para teste: força o cliente a ser remontado na próxima chamada. */
function reset(): void {
  client = null
  clientBase = null
}

export const cmsApi = {
  // Eixo endereçado.
  get,
  find,
  list,
  /**
   * Eixo de placement — DELEGA para `montilla.ads`, não copia a referência.
   *
   * Delegar e não capturar (`render: ads.render`) por dois motivos. O que
   * importa: capturar lê a propriedade no tempo de MÓDULO, então este arquivo
   * passa a exigir que `api/ads` esteja completo no instante do import — e
   * qualquer teste que mocke `api/ads` parcialmente quebra a avaliação de
   * `api/cms`. Foi exatamente o que aconteceu. O outro: delegar continua
   * garantindo que não há dois comportamentos, porque sempre chama o atual.
   */
  render: ((...args) => ads.render(...args)) as typeof ads.render,
  /**
   * Liga impressão e clique a cards que o SDK NÃO pintou — `renderMode: custom`
   * (headless) e slot vindo do servidor. Ver `api/ads.ts`.
   */
  observe: ((...args) => ads.observe(...args)) as typeof ads.observe,
  unmount: ((...args) => ads.unmount(...args)) as typeof ads.unmount,
  scan: ((...args) => ads.scan(...args)) as typeof ads.scan,
  debug: ((...args) => ads.debug(...args)) as typeof ads.debug,
  setContext: ((...args) => ads.setContext(...args)) as typeof ads.setContext,
  isEnabled: (() => ads.isEnabled()) as typeof ads.isEnabled,
  reset,
}
